"use client"

import type React from "react"
import { Button } from "./ui/button"
import {
  Users,
  BookOpen,
  FileText,
  Shield,
  ChevronRight,
  ChevronDown,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Settings,
  BarChart3,
  Zap,
  Home,
  ShoppingBag,
  Crosshair,
  ScrollText,
  Gift,
  Heart,
  Clock,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { wiredService } from "../services/wired"
import { useAuth } from "../contexts/AuthContext"
import { Footer } from "./Footer"
import { DynamicGroupMenu } from "./menu/DynamicGroupMenu"
import { usePermissions, PERMISSOES } from "../hooks/usePermissions"
import { ThemeToggle } from "./ThemeToggle"
import { PainelPracaModal } from "./modals/PainelModal"
import { DashboardHeader } from "./DashboardHeader"
import { MeuStatusAtual } from "./MeuStatusAtual"

interface DashboardLayoutProps {
  children: React.ReactNode
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({})
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDesktopMenuCollapsed, setIsDesktopMenuCollapsed] = useState(false)
  const [isPainelModalOpen, setIsPainelModalOpen] = useState(false)
  const [temAcessoSobrevivente, setTemAcessoSobrevivente] = useState(false)
  const navigate = useNavigate()
  const { signOut, militar } = useAuth()
  const { hasPermission, hasAnyPermission } = usePermissions()

  useEffect(() => {
    let ativo = true
    wiredService
      .obterAcessoSobrevivente()
      .then((acesso) => ativo && setTemAcessoSobrevivente(acesso))
      .catch(() => ativo && setTemAcessoSobrevivente(false))
    return () => {
      ativo = false
    }
  }, [])

  const toggleMenu = (menuName: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }))
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const toggleDesktopMenu = () => {
    setIsDesktopMenuCollapsed(!isDesktopMenuCollapsed)
  }

  const navigateTo = (path: string) => {
    navigate(path)
    setIsMobileMenuOpen(false) // Close mobile menu after navigation
  }

  const handleLogout = () => {
    signOut()
    navigate("/")
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button variant="outline" size="icon" onClick={toggleMobileMenu} className="bg-background border-border">
          {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </Button>
      </div>

      {isMobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={toggleMobileMenu} />}

      {/* Header fixo com busca de militar */}
      <DashboardHeader isCollapsed={isDesktopMenuCollapsed} />

      <div
        className={`hidden lg:block fixed top-6 z-50 transition-all duration-300 ease-in-out ${
          isDesktopMenuCollapsed ? "left-[88px]" : "left-[304px]"
        }`}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={toggleDesktopMenu}
          className="w-8 h-8 p-0 bg-background border-border shadow-lg hover:bg-accent hover:shadow-xl transition-all duration-200"
        >
          {isDesktopMenuCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full bg-sidebar border-r border-sidebar-border overflow-y-auto z-40 transform transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? "translate-x-0 w-72" : "-translate-x-full w-72"
        } lg:translate-x-0 ${isDesktopMenuCollapsed ? "lg:w-20" : "lg:w-72"}`}
        style={{ backgroundColor: 'oklch(var(--sidebar-background))' }}
      >
        <div className={isDesktopMenuCollapsed ? "p-6 lg:px-3 lg:py-5" : "p-6"}>
          {/* Logo/Header */}
          <div className={`flex items-center gap-3 mb-8 ${isDesktopMenuCollapsed ? "lg:justify-center" : ""}`}>
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: 'oklch(var(--sidebar-primary))' }}
            >
              <img
                src={`https://www.habbo.com.br/habbo-imaging/avatarimage?img_format=png&user=${militar?.nick || 'Haouken'}&direction=3&head_direction=2&size=l&headonly=1`}
                alt={militar?.nick || 'Avatar'}
                className="w-full h-full object-cover"
              />
            </div>
            {(!isDesktopMenuCollapsed || isMobileMenuOpen) && (
              <div>
                <h1 className="font-semibold text-lg" style={{ color: 'oklch(var(--sidebar-foreground))' }}>{militar?.nick || 'EXBR'}</h1>
                <p className="text-sm" style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}>{militar?.patente || 'Painel Ex.BR'}</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {/* Meu Status */}
            {hasPermission(PERMISSOES.CENTRAL_FILAS_GERENCIAR) && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
              <div className="pb-1 mb-1 border-b" style={{ borderColor: 'oklch(var(--sidebar-border))' }}>
                <MeuStatusAtual sidebarMode />
              </div>
            )}

            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
              }`}
              style={{ color: 'oklch(var(--sidebar-foreground))' }}
              onClick={() => navigateTo("/dashboard")}
            >
              <Home className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Início"}
            </Button>



            {/* Menu Registros - precisa de permissão para ver militares */}
            {hasAnyPermission([PERMISSOES.LISTAR_MILITARES, PERMISSOES.GERENCIAR_DESTAQUES]) && (
            <div>
              <Button
                variant="ghost"
                className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-between"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={() => toggleMenu("registros")}
              >
                <div className="flex items-center">
                  <BookOpen className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                  {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Registros"}
                </div>
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) &&
                  (expandedMenus.registros ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  ))}
              </Button>
              {expandedMenus.registros && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                <div className="ml-6 mt-2 space-y-1">
                  {hasPermission(PERMISSOES.CRIAR_ALTERAR_MILITAR) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/registros/gestao-militares")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Gestão de Militares
                  </Button>
                  )}

                  {hasPermission(PERMISSOES.LISTAR_MILITARES) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/registros/militares")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Militares
                  </Button>
                  )}

                  {hasPermission(PERMISSOES.GERENCIAR_DESTAQUES) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/registros/destaques")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Destaques
                  </Button>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Menu Informações - JA é acessível por todos; outros itens dependem de permissão */}
            <div>
              <Button
                variant="ghost"
                className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-between"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={() => toggleMenu("informacoes")}
              >
                <div className="flex items-center">
                  <FileText className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                  {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Informações"}
                </div>
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) &&
                  (expandedMenus.informacoes ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  ))}
              </Button>
              {expandedMenus.informacoes && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                <div className="ml-6 mt-2 space-y-1">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/informacoes/treinamentos")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Treinamentos
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/informacoes/patrulhas")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Patrulhas
                  </Button>
                  {hasPermission(PERMISSOES.VISUALIZAR_DESEMPENHO) && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                      onClick={() => navigateTo("/informacoes/desempenho")}
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full mr-3"
                        style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                      />
                      Desempenho
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/informacoes/justificativas-ausencia")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Justif. de Ausência
                  </Button>
                  {hasPermission(PERMISSOES.GERENCIAR_APOSTILAS) && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                      onClick={() => navigateTo("/informacoes/apostilas")}
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full mr-3"
                        style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                      />
                      Apostilas
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Menu Ações Rápidas */}
            <div>
              <Button
                variant="ghost"
                className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-between"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={() => toggleMenu("acoesRapidas")}
              >
                <div className="flex items-center">
                  <Zap className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                  {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Ações Rápidas"}
                </div>
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) &&
                  (expandedMenus.acoesRapidas ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  ))}
              </Button>
              {expandedMenus.acoesRapidas && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                <div className="ml-6 mt-2 space-y-1">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => {
                      window.open(
                        "/relatorio-treinamento",
                        "janela1",
                        "width=550, height=700, top=50, left=350, status=no, toolbar=no, location=no, menubar=no, resizable=no, fullscreen=no"
                      );
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Criar Relatório
                  </Button>

                  {hasPermission(PERMISSOES.CRIAR_ALTERAR_MILITAR) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => {
                      setIsPainelModalOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Criar Painel
                  </Button>
                  )}

                  {hasPermission(PERMISSOES.VISUALIZAR_HORARIOS_OFICIAIS) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => {
                      window.open("/central/horarios-oficiais", "_blank");
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Horários de Oficiais
                  </Button>
                  )}
                </div>
              )}
            </div>

            {/* Menu Central */}
            {hasAnyPermission([
              PERMISSOES.CENTRAL_RM_GERENCIAR,
              PERMISSOES.CENTRAL_RMO_GERENCIAR,
              PERMISSOES.CENTRAL_FILAS_GERENCIAR
            ]) && (
            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
              }`}
              style={{ color: 'oklch(var(--sidebar-foreground))' }}
              onClick={() => {
                window.open("/central", "_blank", "width=830,height=900,left=100,top=100");
                setIsMobileMenuOpen(false);
              }}
            >
              <BarChart3 className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Central"}
            </Button>
            )}

            {/* Menu Comunidade */}
            {hasAnyPermission([
              PERMISSOES.FORUM_VISUALIZAR,
              PERMISSOES.ANIVERSARIANTES_VISUALIZAR,
              PERMISSOES.TITITINDER_VISUALIZAR,
              PERMISSOES.TITITINDER_GERENCIAR,
              PERMISSOES.PEROLAS_ENVIAR,
              PERMISSOES.PEROLAS_GERENCIAR,
            ]) && (
            <div>
              <Button
                variant="ghost"
                className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-between"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={() => toggleMenu("comunidade")}
              >
                <div className="flex items-center">
                  <Heart className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                  {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Comunidade"}
                </div>
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) &&
                  (expandedMenus.comunidade ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  ))}
              </Button>
              {expandedMenus.comunidade && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                <div className="ml-6 mt-2 space-y-1">
                  {hasPermission(PERMISSOES.FORUM_VISUALIZAR) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/forum")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Fórum
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.ANIVERSARIANTES_VISUALIZAR) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/comunidade/aniversariantes")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Aniversariantes
                  </Button>
                  )}
                  {hasAnyPermission([PERMISSOES.TITITINDER_VISUALIZAR, PERMISSOES.TITITINDER_GERENCIAR]) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/comunidade/tititinder")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    TiTiTinder
                  </Button>
                  )}
                  {hasAnyPermission([PERMISSOES.PEROLAS_ENVIAR, PERMISSOES.PEROLAS_GERENCIAR]) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/comunidade/perolas")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    TiTiTi Pérolas
                  </Button>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Menu Configurações - precisa de permissão para ver alguma configuração */}
            {hasAnyPermission([
              PERMISSOES.CRIAR_GRUPO,
              PERMISSOES.CRIAR_PATENTE,
              PERMISSOES.CRIAR_STATUS,
              PERMISSOES.CRIAR_SIGLA,
              PERMISSOES.CRIAR_PERFIL,
              PERMISSOES.CRIAR_ALTERAR_TREINAMENTO,
              PERMISSOES.GERENCIAR_HONRARIAS,
              PERMISSOES.GERENCIAR_MEDALHAS,
              PERMISSOES.GERENCIAR_EMBLEMAS,
              PERMISSOES.GERENCIAR_SITE_CARROSSEL,
              PERMISSOES.GERENCIAR_WIRED_CONFIGURACOES,
              PERMISSOES.VISUALIZAR_WIRED_LOGS
            ]) && (
            <div>
              <Button
                variant="ghost"
                className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-between"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={() => toggleMenu("configuracoes")}
              >
                <div className="flex items-center">
                  <Settings className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                  {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Configurações"}
                </div>
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) &&
                  (expandedMenus.configuracoes ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  ))}
              </Button>
              {expandedMenus.configuracoes && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                <div className="ml-6 mt-2 space-y-1">
                  {hasPermission(PERMISSOES.CRIAR_GRUPO) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/grupos")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Grupos
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.CRIAR_PATENTE) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/patentes")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Patentes
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.CRIAR_STATUS) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/status")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Status
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.CRIAR_SIGLA) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/siglas")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Siglas
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.CRIAR_PERFIL) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/perfis")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Perfis
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.CRIAR_ALTERAR_TREINAMENTO) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/treinamentos")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Treinamentos
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.GERENCIAR_HONRARIAS) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/gerenciar-honrarias")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Honrarias
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.GERENCIAR_MEDALHAS) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/gerenciar-medalhas")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Medalhas
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.GERENCIAR_EMBLEMAS) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/gerenciar-emblemas")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Emblemas
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.GERENCIAR_SITE_CARROSSEL) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/site")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Site
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.GERENCIAR_INTEGRACOES_DISCORD) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/discord")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Discord
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.GERENCIAR_WIRED_CONFIGURACOES) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/wired")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Habbo Wired
                  </Button>
                  )}
                  {hasPermission(PERMISSOES.VISUALIZAR_WIRED_LOGS) && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                    onClick={() => navigateTo("/configuracoes/wired/logs")}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mr-3"
                      style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                    />
                    Logs do Wired
                  </Button>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Menu Dinâmico de Grupos */}
            <DynamicGroupMenu
              expandedMenus={expandedMenus}
              toggleMenu={toggleMenu}
              isDesktopMenuCollapsed={isDesktopMenuCollapsed}
              isMobileMenuOpen={isMobileMenuOpen}
              navigateTo={navigateTo}
            />

            {/* Apostilas - acessível a todos os militares */}
            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
              }`}
              style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
              onClick={() => navigateTo("/apostilas")}
            >
              <BookOpen className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Apostilas"}
            </Button>

            {/* Sobrevivente - abre em janela própria; só aparece para quem tem a variável de acesso */}
            {temAcessoSobrevivente && (
            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
              }`}
              style={{ color: 'rgb(251 146 60)' }}
              onClick={() => {
                window.open("/sobrevivente", "_blank", "width=830,height=900,left=100,top=100");
                setIsMobileMenuOpen(false);
              }}
            >
              <Crosshair className={`w-4 h-4 animate-pulse ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                <span className="animate-pulse font-semibold">Sobrevivente</span>
              )}
            </Button>
            )}

            {hasPermission(PERMISSOES.VISUALIZAR_AUDIT_LOGS) && (
              <Button
                variant="ghost"
                className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={() => navigateTo("/logs")}
              >
                <ScrollText className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Logs"}
              </Button>
            )}

            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
              }`}
              style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
              onClick={() => navigateTo("/loja")}
            >
              <ShoppingBag className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Loja"}
            </Button>

            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
              }`}
              style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
              onClick={() => navigateTo("/meu-usuario")}
            >
              <Shield className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Meu Usuário"}
            </Button>

            <div className="pt-4 mt-4 border-t" style={{ borderColor: 'oklch(var(--sidebar-border))' }}>
              {/* Theme Toggle */}
              <div className={`flex items-center mb-2 ${isDesktopMenuCollapsed ? "lg:justify-center" : "justify-start"}`}>
                <ThemeToggle />
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                  <span className="ml-2 text-sm" style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}>
                    Tema
                  </span>
                )}
              </div>

              <Button
                variant="ghost"
                className={`w-full hover:bg-destructive hover:text-destructive-foreground ${
                  isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
                }`}
                style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
                onClick={handleLogout}
              >
                <LogOut className={`w-4 h-4 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`} />
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) && "Sair"}
              </Button>
            </div>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`pt-16 lg:pt-14 flex-1 transition-all duration-300 ease-in-out ${
          isDesktopMenuCollapsed ? "lg:ml-20" : "lg:ml-72"
        }`}
      >
        {children}
      </div>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isDesktopMenuCollapsed ? "lg:ml-20" : "lg:ml-72"
        }`}
      >
        <Footer />
      </div>

      {/* Modal Criar Painel */}
      <PainelPracaModal
        isOpen={isPainelModalOpen}
        onClose={() => setIsPainelModalOpen(false)}
        onSuccess={() => setIsPainelModalOpen(false)}
      />
    </div>
  )
}
